import { Modal, Radio } from 'antd';
import {
  Button,
  CheckboxButton,
  CloseIcon,
  Datepicker,
  Dropdown,
  Input,
  Paragrapgh,
  Subtitle,
  RadioButton,
} from '../../shared';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import {
  numToStr,
  toMillisecond,
  toSecond,
  getUnitOfSpecie,
} from '../../../lib/common.helpers';
import { parseImageUrlToFileItem } from '../../../lib/common.helpers';
import TimeTrackInput, {
  toSecTimeTracks,
} from '../../shared/input/TimeTrackInput';
import ModalFeedbackView from '../../shared/feedback/ModalFeedbackView';
import { sendSingleRequest } from '../../../apis';
import {
  loadFarmsData,
  loadQueueData,
} from '../../../store/farms/farms.actions';
import { showFeedback } from '../../../store/ui/ui.actions';
import { ITimeRange } from '../../../entities/general.entities';
import { IOysterLineResource } from '../../../entities/farms.entities';
import { getSizeMeasureLabel, translate } from '../../../lib/lang.helper';
import { selectLang } from '../../../store/ui/ui.selector';
import {
  SelectFarmsByFarmID,
  SelectIsMyFarm,
} from '../../../store/extra/extra.selector';
import OysterLineProbTable, {
  OysterProbRef,
} from '../../farm-util/OysterLineProbTable';
import BatchAssessView from './BatchAssessView';
import DirectionForm from '../../farm-util/DirectionForm';
import { dozToPcs, pcsToDoz } from '../../../lib/common.helpers';
import OysterSampleCalculator from '../../farm-util/OysterSampleCalculator';
import { selectCropSpecies } from '../../../store/utils/utils.selector';
import { getLineGrowingData } from '../../../lib/farm.helpers';
import MinMaxInputForm from '../SeedingModal/MinMaxInputForm';
import FilesWall from '../../shared/pictures-wall/FilesWall';
import {
  selectProfile,
  selectAccount,
} from '../../../store/auth/auth.selector';
import SelectBoat from '../../farmform/SelectBoat';
import { TaggingManageForm } from '../../lines/LineTagButton';
import { loadTags } from '../../../store/automation/automation.actions';

interface IFormData {
  estimated_amount?: number | null;
  estimated_dozens?: number | null;
  size_min: number | null;
  size_max: number | null;
  size_avg: number | null;
  min_width?: number | null;
  max_width?: number | null;
  avg_width?: number | null;
  density?: number | null;
  density_max?: number | null;
  assessment_date: number;
  planned_date_harvest: number;
  ready_to_harvest: boolean;
  batch_seed_id?: number | null;
  origin_spat_id?: number | null;
  comment: string | null;
  direction: string | null;
  time_tracks?: ITimeRange[];
  image_pdf_files: any[];
  measurement_units?: string;
  shellfish_measurements?: any[];
  seeding_id?: number;
  boat_id?: number | null;

  tagged_ids?: number[] | null;
}

const defaultForm: IFormData = {
  size_min: null,
  size_max: null,
  size_avg: null,
  min_width: null,
  max_width: null,
  avg_width: null,
  image_pdf_files: [],
  planned_date_harvest: moment().toDate().getTime(),
  assessment_date: moment().toDate().getTime(),
  comment: null,
  ready_to_harvest: false,
  direction: null,
  time_tracks: [],
  measurement_units: 'dz',
};

const getEstimated = (line: IOysterLineResource) => {
  if (line.growing_cycle?.last_assessment?.estimated_amount) {
    return {
      estimated_amount: line.growing_cycle.last_assessment.estimated_amount,
      estimated_dozens: pcsToDoz(
        line.growing_cycle.last_assessment.estimated_amount,
      ),
      planned_date_harvest: toMillisecond(
        line.growing_cycle.last_assessment.planned_date_harvest,
      ),
    };
  } else if (line.growing_cycle) {
    return {
      estimated_amount: line.growing_cycle.current_amount_pcs,
      estimated_dozens: line.growing_cycle.current_amount_dz,
      planned_date_harvest: toMillisecond(
        line.growing_cycle.main_seed.planned_date_harvest,
      ),
    };
  } else {
    return {
      estimated_amount: undefined,
      estimated_dozens: undefined,
      planned_date_harvest: defaultForm.planned_date_harvest,
    };
  }
};

interface Props {
  visible: boolean;
  title?: string;
  lineData: IOysterLineResource;
  onCancel: () => void;
  onConfirm: (data: any) => void;
  data?: any;
  updateID?: number;
  onlyView?: boolean;
  overConfirm?: (data: any) => void;
  lineChangeable?: boolean;
}

const OysterAssessModal = ({
  lineData,
  visible,
  title,
  onCancel,
  onConfirm,
  data,
  updateID,
  onlyView,
  overConfirm,
  lineChangeable,
}: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const farmsData = SelectFarmsByFarmID(lineData.farm_id);
  const isMyFarm = SelectIsMyFarm(lineData.farm_id);
  const curBoat = useSelector(selectProfile)?.current_boat;
  const accSetting = useSelector(selectAccount);
  const unitType = accSetting?.display_unit;
  const manageWidth = !!accSetting?.manage_shellfish_width;
  const lineProps = getLineGrowingData(lineData);

  const [formData, setFormData] = useState<IFormData>({
    ...defaultForm,
    planned_date_harvest: toMillisecond(
      lineProps.planned_date_harvest ?? defaultForm.planned_date_harvest,
    ),
    estimated_amount: getEstimated(lineData).estimated_amount,
    estimated_dozens: getEstimated(lineData).estimated_dozens,
    size_min: lineProps.shell_size_min ?? null,
    size_max: lineProps.shell_size_max ?? null,
    size_avg: lineProps.shell_size_avg ?? null,
    density: lineProps.density ?? null,
    density_max: lineProps.density_max ?? null,
    boat_id: data?.boat_id ?? curBoat?.id,
  });

  const [warning, setWaring] = useState(false);
  const [errors, setErrors] = useState<{
    min: string | null;
    max: string | null;
    avg: string | null;
  }>({
    min: null,
    max: null,
    avg: null,
  });
  const [curFarm, setCurFarm] = useState(
    farmsData.find(x => x.id === lineData.farm_id),
  );
  const [curLine, setCurLine] = useState(lineData);
  const [trackOrigin, setTrackOrigin] = useState(!!data?.origin_spat_id);
  const [disabled, setDisabled] = useState(false);
  const [probabilities, setProbabilities] = useState<Record<string, string>>(
    {},
  );
  const oysterFields = useSelector(selectCropSpecies);

  const tableRef = useRef<OysterProbRef>(null);

  const handleLineChange = (v: string) => {
    if (!curFarm) return;
    const line = curFarm.lines.find(x => x.id === Number(v)) as
      | IOysterLineResource
      | undefined;
    if (!line?.growing_cycle) return;
    setCurLine(line);
    setFormData(prv => ({
      ...prv,
      planned_date_harvest: toMillisecond(
        line.growing_cycle?.main_seed.planned_date_harvest ?? 0,
      ),
    }));
  };
  const updateForm = (key: keyof IFormData, val: any) => {
    if (
      [
        'size_min',
        'size_max',
        'size_avg',
        'min_width',
        'max_width',
        'avg_width',
        'estimated_amount',
        'estimated_dozens',
      ].includes(key)
    ) {
      val = val.length <= 0 ? null : Number(val);
    }
    let tmp = { ...formData, [key]: val };
    if (key === 'size_min') {
      setErrors(p => ({ ...p, min: null, avg: null }));
      tmp.size_avg = Math.round(
        (Number(tmp.size_max) + Number(tmp.size_min)) / 2,
      );
    } else if (key === 'min_width' || key === 'max_width') {
      tmp.avg_width = Math.round(
        (Number(tmp.max_width) + Number(tmp.min_width)) / 2,
      );
    } else if (key === 'size_max') {
      setErrors(p => ({ ...p, max: null, avg: null }));
      tmp.size_avg = Math.round(
        (Number(tmp.size_max) + Number(tmp.size_min)) / 2,
      );
    } else if (key === 'estimated_dozens') {
      tmp.estimated_amount = dozToPcs(val);
    } else if (key === 'estimated_amount') {
      tmp.estimated_dozens = pcsToDoz(val);
    }
    setFormData(tmp);
  };
  const showError = (message: string) =>
    dispatch(
      showFeedback({
        isMessageModal: true,
        type: 'error',
        message: translate(lang, message),
      }),
    );

  const validForm = () => {
    let res: any = true;
    let tmpErr = { ...errors };
    if (!formData.size_min) {
      tmpErr.min = translate(lang, 'Required field');
      res = false;
    }
    if (!formData.size_max) {
      tmpErr.max = translate(lang, 'Required field');
      res = false;
    }
    if (!formData.size_avg) {
      tmpErr.avg = translate(lang, 'Required field');
      res = false;
    }
    if (Number(formData.size_min) > Number(formData.size_max)) {
      tmpErr.avg = translate(lang, '_max_value_equal_greater');
      res = false;
    }
    if (
      formData.density_max &&
      (!formData.density || formData.density_max <= formData.density!)
    ) {
      showError('Density max should be greater than density');
      res = false;
    }
    if (
      formData.time_tracks &&
      toSecTimeTracks(formData.time_tracks) === false
    ) {
      showError('_invalid_time_range');
      res = false;
    }
    setErrors(tmpErr);
    if (!res) return null;

    res = {
      ...formData,
      assessment_date: toSecond(formData.assessment_date),
      planned_date_harvest: toSecond(formData.planned_date_harvest),
      seeding_id: formData?.seeding_id ?? lineData.growing_cycle?.main_seed.id,
      line_id: lineData.id,
    };
    res.btm_size_min = res.btm_size_max = res.btm_size_avg = undefined;

    if (res.time_tracks && res.time_tracks.length > 0) {
      const tt = toSecTimeTracks(res.time_tracks);
      if (!tt) {
        showError('_invalid_time_tracks_info');
        return null;
      }
      res.time_tracks = tt;
    }
    return res;
  };
  const handleConfirmClick = async () => {
    if (onlyView) return;
    const form = validForm();
    if (!form) return;

    if (tableRef.current) {
      const res = tableRef.current.checkIsValid();
      if (res !== true) {
        showError(res);
        return;
      }
    }

    if (
      !warning &&
      !updateID &&
      lineData.growing_cycle?.last_assessment &&
      (lineData.growing_cycle.last_assessment.shell_size.min >= form.size_min ||
        lineData.growing_cycle.last_assessment.shell_size.max >= form.size_max)
    ) {
      setWaring(true);
      return;
    }
    setDisabled(true);
    if (overConfirm) {
      overConfirm(form);
      return;
    }
    const res = !updateID
      ? await sendSingleRequest(
          form,
          'POST',
          'api/farm/line/assessment/assessments',
          true,
        )
      : await sendSingleRequest(
          form,
          'PUT',
          `api/farm/line/assessment/assessments/${updateID}`,
          true,
        );
    if (res.status) {
      if (form.tagged_ids) {
        dispatch(loadTags());
      }
      dispatch(loadFarmsData());
      if (tableRef.current) {
        await tableRef.current.runConfirm();
      }
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, res.data?.message ?? 'Success'),
        }),
      );
      dispatch(loadQueueData());
      onConfirm(res.data);
    } else {
      showError(res.data?.message ?? 'Request failed');
      setDisabled(false);
    }
  };

  useEffect(() => {
    if (data) {
      let form = { ...data };
      if (form.assessment_date)
        form.assessment_date = toMillisecond(form.assessment_date);
      if (form.planned_date_harvest)
        form.planned_date_harvest = toMillisecond(form.planned_date_harvest);
      form.ready_to_harvest = !!data.ready_harvest || !!data.ready_to_harvest;
      if (form.time_tracks && form.time_tracks.length > 0) {
        form.time_tracks = data.time_tracks.map((x: any) => ({
          start_time: toMillisecond(x.start_time),
          finish_time: toMillisecond(x.finish_time),
        }));
      }
      if (data.image_pdf_files && data.image_pdf_files.length > 0) {
        let tmp: any = [];
        for (let x of data.image_pdf_files) {
          if (typeof x === 'object' && x !== null) {
            tmp.push(x);
          } else {
            tmp.push(parseImageUrlToFileItem(x));
          }
        }
        if (tmp && tmp.length > 0) {
          form.image_pdf_files = tmp;
        }
      }
      if (data.pdf_files && data.pdf_files.length > 0) {
        let tmp: any = [];
        for (let x of data.pdf_files) {
          if (typeof x === 'object' && x !== null) {
            tmp.push(x);
          } else {
            tmp.push(parseImageUrlToFileItem(x));
          }
        }
        if (tmp && tmp.length > 0) {
          form.pdf_files = tmp;
        }
      }
      if (data.estimated_amount) {
        form.estimated_amount = data.estimated_amount;
        form.estimated_dozens = pcsToDoz(data.estimated_amount);
      }
      form.measurement_units = data.measurement_units ?? 'dz';
      setFormData(prv => ({ ...prv, ...form }));
    }
  }, [data]);

  const farmOptions = farmsData.map(x => ({
    id: x.id.toString(),
    value: x.id.toString(),
    label: `${x.name} - ${x.farm_number}`,
  }));
  const lineOptions = curFarm?.lines
    .filter(x => !!x.growing_cycle && x.crop_type === 'OYSTER')
    .map(x => ({
      id: x.id.toString(),
      value: x.id.toString(),
      label: x.line_name,
    }));

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
      width={650}
    >
      <div className='wrap'>
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, title ?? 'Oyster assessment')}
          </Subtitle>
        </div>
        <div className='assessment-modal'>
          {lineChangeable && (
            <div className='mb-17 d-flex'>
              <div className='w-100 mr-7'>
                <Dropdown
                  label={translate(lang, 'Select farm')}
                  options={farmOptions}
                  value={curFarm?.id.toString()}
                  onChange={v =>
                    setCurFarm(farmsData.find(x => x.id === Number(v)))
                  }
                  disabled={onlyView}
                />
              </div>
              <div className='w-100 ml-7'>
                <Dropdown
                  label={translate(lang, 'Select line')}
                  options={lineOptions ?? []}
                  value={
                    !!lineOptions &&
                    lineOptions.some(x => Number(x.id) === curLine.id)
                      ? curLine.id.toString()
                      : curLine.line_name
                  }
                  onChange={handleLineChange}
                  disabled={onlyView}
                />
              </div>
            </div>
          )}
          {warning ? (
            <div className='mt-32 mb-64 pb-32 pt-17 pl-17 pr-17'>
              <Paragrapgh
                size={1}
                fontWeight={400}
                color='black-2'
                align='default'
              >
                {translate(lang, '_assessment_warning')}
              </Paragrapgh>
            </div>
          ) : (
            <div className='assessment-modal'>
              <div className='d-flex mb-16'>
                <div className='w-100'>
                  {translate(lang, 'Measurement units')}
                </div>
                <div className='w-100'>
                  <Radio.Group
                    className='d-flex align-center'
                    onChange={e =>
                      updateForm('measurement_units', e.target.value)
                    }
                    value={formData.measurement_units}
                  >
                    <RadioButton label='dz' value='dz' />
                    <RadioButton className='ml-17' label='pcs' value='pcs' />
                  </Radio.Group>
                </div>
              </div>
              <div className='pb-17'>
                <Input
                  label={
                    formData.measurement_units === 'pcs'
                      ? translate(lang, 'Estimated pieces')
                      : translate(lang, 'Estimated dozens')
                  }
                  type='number'
                  value={
                    formData.measurement_units === 'pcs'
                      ? numToStr(formData.estimated_amount)
                      : numToStr(formData.estimated_dozens)
                  }
                  onChange={e =>
                    formData.measurement_units === 'pcs'
                      ? updateForm('estimated_amount', e.target.value)
                      : updateForm('estimated_dozens', e.target.value)
                  }
                  unit={formData.measurement_units === 'pcs' ? 'pcs' : 'dz'}
                />
              </div>
              {isMyFarm && (
                <div className='pb-17'>
                  <OysterSampleCalculator
                    ref={tableRef}
                    lineID={curLine.id}
                    onInsert={setProbabilities}
                    oysterFields={oysterFields}
                    onGrowWaste={tableRef}
                  />
                  <OysterLineProbTable
                    ref={tableRef}
                    lineID={curLine.id}
                    totalAmount={
                      formData.measurement_units === 'pcs'
                        ? (formData.estimated_amount ?? 0)
                        : (formData.estimated_dozens ?? 0)
                    }
                    probabilities={probabilities}
                    amountType={
                      formData.measurement_units === 'pcs' ? 'pcs' : 'dz'
                    }
                  />
                </div>
              )}
              <div className='assessment-modal__condition d-flex justify-between'>
                <div className={`mxw-180 ${errors.min ? 'invalid-form' : ''}`}>
                  <Input
                    type='number'
                    label={`${getSizeMeasureLabel(lang, 'min', 'length', undefined, manageWidth)} (${getUnitOfSpecie(unitType, 'size_mm')})`}
                    value={numToStr(formData.size_min)}
                    onChange={e => updateForm('size_min', e.target.value)}
                    required={true}
                  />
                  {errors.min && (
                    <div className='invalid-feedback'>{errors.min}</div>
                  )}
                </div>
                <div className={`mxw-180 ${errors.max ? 'invalid-form' : ''}`}>
                  <Input
                    type='number'
                    label={`${getSizeMeasureLabel(lang, 'max', 'length', undefined, manageWidth)} (${getUnitOfSpecie(unitType, 'size_mm')})`}
                    value={numToStr(formData.size_max)}
                    onChange={e => updateForm('size_max', e.target.value)}
                    required={true}
                  />
                  {errors.max && (
                    <div className='invalid-feedback'>{errors.max}</div>
                  )}
                </div>
                <div className={`mxw-180 ${errors.avg ? 'invalid-form' : ''}`}>
                  <Input
                    type='number'
                    label={`${getSizeMeasureLabel(lang, 'avg', 'length', undefined, manageWidth)} (${getUnitOfSpecie(unitType, 'size_mm')})`}
                    value={numToStr(formData.size_avg)}
                    onChange={e => updateForm('size_avg', e.target.value)}
                    required={true}
                  />
                  {errors.avg && (
                    <div className='invalid-feedback'>{errors.avg}</div>
                  )}
                </div>
              </div>
              {manageWidth && (
                <div className='assessment-modal__condition d-flex justify-between'>
                  <div className='mxw-180'>
                    <Input
                      type='number'
                      label={`${getSizeMeasureLabel(lang, 'min', 'width')} (${getUnitOfSpecie(unitType, 'size_mm')})`}
                      value={numToStr(formData.min_width)}
                      onChange={e => updateForm('min_width', e.target.value)}
                    />
                  </div>
                  <div className='mxw-180'>
                    <Input
                      type='number'
                      label={`${getSizeMeasureLabel(lang, 'max', 'width')} (${getUnitOfSpecie(unitType, 'size_mm')})`}
                      value={numToStr(formData.max_width)}
                      onChange={e => updateForm('max_width', e.target.value)}
                    />
                  </div>
                  <div className='mxw-180'>
                    <Input
                      type='number'
                      label={`${getSizeMeasureLabel(lang, 'avg', 'width')} (${getUnitOfSpecie(unitType, 'size_mm')})`}
                      value={numToStr(formData.avg_width)}
                      onChange={e => updateForm('avg_width', e.target.value)}
                    />
                  </div>
                </div>
              )}
              <Datepicker
                className='mb-17'
                defaultValue={formData.assessment_date}
                label={translate(lang, 'Assessment date')}
                onChange={e =>
                  updateForm(
                    'assessment_date',
                    e!.toDate().getTime() <
                      Number(
                        lineData.growing_cycle?.main_seed.planned_date_seed,
                      )
                      ? moment().toDate().getTime()
                      : e!.toDate().getTime(),
                  )
                }
                required={true}
              />
              <Datepicker
                className='mb-17'
                defaultValue={formData.planned_date_harvest}
                label={translate(lang, 'Planned harvest date')}
                onChange={e =>
                  updateForm(
                    'planned_date_harvest',
                    e!.toDate().getTime() <
                      Number(
                        lineData.growing_cycle?.main_seed.planned_date_seed,
                      )
                      ? moment().toDate().getTime()
                      : e!.toDate().getTime(),
                  )
                }
                required={true}
              />
              <MinMaxInputForm
                label={translate(lang, 'Density')}
                min={formData.density}
                max={formData.density_max}
                unit={`per ${lineData.farming_method}`}
                onMinChange={v => updateForm('density', v)}
                onMaxChange={v => updateForm('density_max', v)}
              />
              {curLine.growing_cycle && (
                <BatchAssessView
                  status={trackOrigin}
                  onChange={setTrackOrigin}
                  lineData={curLine}
                  form={formData}
                  onBatchSelect={v =>
                    setFormData(prv =>
                      v
                        ? { ...prv, ...v }
                        : { ...prv, origin_spat_id: null, batch_seed_id: null },
                    )
                  }
                />
              )}
              <CheckboxButton
                className='mb-17'
                label={translate(lang, 'Ready to harvest')}
                checked={formData.ready_to_harvest}
                onChange={e => updateForm('ready_to_harvest', e.target.checked)}
              />
              <DirectionForm
                value={formData.direction}
                onChange={v => updateForm('direction', v)}
              />
              {formData.time_tracks && (
                <TimeTrackInput
                  data={formData.time_tracks}
                  onChange={t => updateForm('time_tracks', t)}
                  baseDate={formData.assessment_date}
                />
              )}
              <Input
                label={translate(lang, 'Comment')}
                placeholder={`${translate(lang, 'Type your comment')}...`}
                className='mb-17'
                type='textarea'
                value={formData.comment ?? ''}
                dataType='comment'
                onChange={e => updateForm('comment', e.target.value)}
                isOptional={true}
              />
              <SelectBoat
                value={formData.boat_id}
                onChange={v => updateForm('boat_id', v)}
                className='mb-17'
              />
              <FilesWall
                label={translate(lang, 'PDF/Images')}
                fileList={formData.image_pdf_files}
                onChange={f => updateForm('image_pdf_files', f)}
              />
            </div>
          )}
        </div>
        {!updateID && (
          <TaggingManageForm
            className='mb-17 mt-17'
            lineData={curLine}
            tagIDs={formData.tagged_ids}
            onChange={ids => updateForm('tagged_ids', ids)}
          />
        )}
      </div>
      <div className='modal-button d-flex justify-end align-center'>
        {!warning ? (
          <Button
            width={'small'}
            size={2}
            type='fill'
            color='green'
            className='rsp-btn ml-16'
            onClick={handleConfirmClick}
            disabled={disabled || onlyView}
          >
            {translate(lang, 'Confirm')}
          </Button>
        ) : (
          <>
            <Button
              className='rsp-btn'
              width='small'
              size={2}
              type='fill'
              color='blue'
              onClick={() => setWaring(false)}
              disabled={disabled}
            >
              {translate(lang, 'Let me correct')}
            </Button>
            <Button
              width='small'
              size={2}
              type='fill'
              color='green'
              className='rsp-btn ml-16'
              onClick={handleConfirmClick}
              disabled={disabled || onlyView}
            >
              {translate(lang, "Yes, it's valid")}
            </Button>
          </>
        )}
      </div>
      <ModalFeedbackView />
    </Modal>
  );
};

export default OysterAssessModal;
